/* ==========================================================================
   DreamBrothers — db-3d-viewer.js
   "See it in 3D / AR" for ANY product, with ZERO manual GLB files.
   The framed 3D model is generated at runtime from the naked design image
   (product metafield custom.design_image_url → R2/CDN, fallback featured
   image). Depends on db-glb.js (window.DBGlb).
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('db-3d-viewer')) return;

  function DB3DViewer() {
    return Reflect.construct(HTMLElement, [], DB3DViewer);
  }
  DB3DViewer.prototype = Object.create(HTMLElement.prototype);
  DB3DViewer.prototype.constructor = DB3DViewer;

  DB3DViewer.prototype.connectedCallback = function () {
    var cfgNode = this.querySelector('[data-db3d-config]');
    if (!cfgNode) return;
    try {
      this.cfg = JSON.parse(cfgNode.textContent);
    } catch (e) {
      console.error('[db-3d-viewer] invalid config JSON', e);
      return;
    }
    var btn = this.querySelector('[data-db3d-open]');
    if (btn) btn.addEventListener('click', this.open.bind(this));
    this.lightbox = null;
  };

  DB3DViewer.prototype.open = function () {
    var self = this;
    var cfg = this.cfg;
    if (!window.DBGlb) return;

    if (!this.lightbox) {
      var tpl = this.querySelector('[data-db3d-template]');
      this.lightbox = tpl.content.cloneNode(true).querySelector('.db-3d-lb');
      this.lightbox.querySelector('[data-db3d-close]').addEventListener('click', this.close.bind(this));
      this.lightbox.querySelector('[data-db3d-backdrop]').addEventListener('click', this.close.bind(this));
      this._esc = function (e) { if (e.key === 'Escape') self.close(); };
    }
    var lb = this.lightbox;
    document.body.appendChild(lb);
    document.addEventListener('keydown', this._esc);
    requestAnimationFrame(function () { lb.classList.add('is-open'); });

    var mount = lb.querySelector('[data-db3d-mount]');
    mount.textContent = '';
    var loading = document.createElement('div');
    loading.className = 'db-3d-lb__loading';
    loading.textContent = cfg.loadingLabel || 'Building 3D model…';
    mount.appendChild(loading);

    // Hosted model → native AR (Scene Viewer / Quick Look). No WebXR prompt.
    if (cfg.modelGlb) {
      DBGlb.loadModelViewer().then(function () {
        if (self._glbUrl) { URL.revokeObjectURL(self._glbUrl); self._glbUrl = null; }
        var mv = document.createElement('model-viewer');
        mv.setAttribute('src', cfg.modelGlb);
        mv.setAttribute('alt', (cfg.title || 'Product') + ' in 3D');
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('environment-image', 'neutral');
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          mv.setAttribute('auto-rotate', '');
          mv.setAttribute('auto-rotate-delay', '1200');
          mv.setAttribute('rotation-per-second', '14deg');
        }
        mv.setAttribute('shadow-intensity', '1');
        mv.setAttribute('exposure', '1.4');
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-placement', 'wall');
        mv.setAttribute('ar-modes', 'scene-viewer quick-look webxr');
        if (cfg.modelUsdz) mv.setAttribute('ios-src', cfg.modelUsdz);
        DBGlb.frameFront(mv);
        mount.innerHTML = '';
        mount.appendChild(mv);
      }).catch(function (err) {
        console.error('[db-3d-viewer] hosted model failed', err);
        mount.innerHTML = '<div class="db-3d-lb__loading">3D view is not available right now.</div>';
      });
      return;
    }

    Promise.all([DBGlb.loadModelViewer(), DBGlb.loadImage(cfg.designUrl)])
      .then(function (results) {
        var img = results[1];
        // texture canvas (frame drawn around the naked design);
        // longest side capped at 1024 for weak GPUs
        var ratio = img.height / img.width;
        var W = ratio >= 1 ? Math.round(1024 / ratio) : 1024;
        var H = ratio >= 1 ? 1024 : Math.round(1024 * ratio);
        var canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        var ctx = canvas.getContext('2d');
        var frameHex = cfg.frameColor && cfg.frameColor !== 'none' ? cfg.frameColor : null;
        var framePx = frameHex ? Math.round(W * 0.035) : 0;
        if (frameHex) {
          ctx.fillStyle = frameHex;
          ctx.fillRect(0, 0, W, H);
        }
        ctx.drawImage(img, framePx, framePx, W - framePx * 2, H - framePx * 2);
        return DBGlb.canvasToPngBytes(canvas).then(function (png) {
          return { png: png, ratio: H / W };
        });
      })
      .then(function (tex) {
        var widthM = 0.5;
        var glb = DBGlb.buildPosterGlb({
          pngBytes: tex.png,
          width: widthM,
          height: widthM * tex.ratio,
          depth: cfg.depth || 0.025,
          edgeColor: cfg.frameColor && cfg.frameColor !== 'none' ? DBGlb.hexToRgb01(cfg.frameColor) : [0.09, 0.09, 0.1],
          roughness: 0.8
        });
        if (self._glbUrl) URL.revokeObjectURL(self._glbUrl);
        self._glbUrl = URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' }));

        var mv = document.createElement('model-viewer');
        mv.setAttribute('src', self._glbUrl);
        mv.setAttribute('alt', (cfg.title || 'Product') + ' in 3D');
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('environment-image', 'neutral');
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          mv.setAttribute('auto-rotate', '');
          mv.setAttribute('auto-rotate-delay', '1200');
          mv.setAttribute('rotation-per-second', '14deg');
        }
        mv.setAttribute('shadow-intensity', '1');
        mv.setAttribute('exposure', '1.4');
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-placement', 'wall');
        mv.setAttribute('ar-modes', 'webxr');
        DBGlb.frameFront(mv);
        mount.innerHTML = '';
        mount.appendChild(mv);
      })
      .catch(function (err) {
        console.error('[db-3d-viewer] failed', err);
        mount.innerHTML = '<div class="db-3d-lb__loading">3D view is not available right now.</div>';
      });
  };

  DB3DViewer.prototype.close = function () {
    var lb = this.lightbox;
    if (!lb || !lb.isConnected) return;
    document.removeEventListener('keydown', this._esc);
    lb.classList.remove('is-open');
    setTimeout(function () {
      if (lb.parentNode) lb.parentNode.removeChild(lb);
      var mount = lb.querySelector('[data-db3d-mount]');
      if (mount) mount.innerHTML = '';
    }, 300);
  };

  customElements.define('db-3d-viewer', DB3DViewer);
})();
